import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { SankeyController, Flow } from "chartjs-chart-sankey";
import { Chart } from "react-chartjs-2";
import { fetchEnergyHierarchyTree, scopeHierarchyTree } from "../utils/energyHierarchyTree";
import ChartBurgerMenu from "./ChartBurgerMenu";
import FieldHelp from "../help/FieldHelp";
import AIAnalyzerModal from "./AIAnalyzerModal";
import AIAnalyzerButton from "./AIAnalyzerButton";
import BoltLoader from "./BoltLoader";
import { buildChartPayload } from "../utils/aiAnalyzerEngine";
import {
  formatAmount,
  rateForCategory,
  resolveTariffs,
  unitLabel,
} from "../utils/currencyConversion";

ChartJS.register(
  SankeyController,
  Flow,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
);

const COLOR_PALETTE = [
  "#009f8d", // Teal Main
  "#ff9c07", // Orange Main
  "#1e88e5", // Blue
  "#8e24aa", // Purple
  "#0ea5e9", // Light Blue
  "#facc15", // Yellow
  "#00c6a9", // Light Teal
  "#ffb74d", // Light Orange
  "#6d28d9", // Deep Purple
  "#10b981", // Green
  "#f43f5e", // Rose Red
  "#059669", // Dark Green
];

const getNodeColor = (depth) =>
  COLOR_PALETTE[(depth - 1) % COLOR_PALETTE.length];

const parseEnergy = (val) => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const num = parseFloat(val.replace(/[^\d.-]/g, ""));
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

const SankeyDiagram = ({
  username,
  dateRange,
  onLoadingChange,
  scopeHierarchyPath,
  machineColors = {},
  currencyMode = "Energy",
  tariffs,
}) => {
  const isPkr = currencyMode === "PKR";
  // HistoricalTab passes the resolved set; fall back so the diagram still works
  // if it is ever mounted standalone.
  const rates = useMemo(() => tariffs || resolveTariffs(null), [tariffs]);
  const valueUnit = unitLabel(isPkr);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [maxLevels, setMaxLevels] = useState(2);
  const chartRef = useRef(null);

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalPayload, setAiModalPayload] = useState(null);

  const handleAnalyzeSankey = () => {
    const flows = chartData?.datasets?.[0]?.data || [];
    const datasets = flows.map((f) => ({
      label: `${f.from} → ${f.to}`,
      data: [Number(f.flow) || 0],
    }));

    const payload = buildChartPayload({
      chartTitle: isPkr
        ? "Cost Flow Diagram (Sankey)"
        : "Energy Flow Diagram (Sankey)",
      metricName: isPkr ? "Cost Flow" : "Energy Flow",
      unit: valueUnit,
      timestamps: [isPkr ? "Total Cost Flow" : "Total Energy Flow"],
      datasets: datasets,
      dateRange: dateRange,
    });
    setAiModalPayload(payload);
    setAiModalOpen(true);
  };
  const treeDepthRef = useRef(2); // kept in sync with the computed treeDepth below
  // treeDepth is only known once the tree loads, so the Full default is applied then --
  // and only once, so a later manual pick from the dropdown is not overwritten.
  const levelsInitialisedRef = useRef(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
      setIsFullScreen(isFull);
      if (isFull) {
        setMaxLevels(treeDepthRef.current); // show the client's full depth (generic)
      } else {
        setMaxLevels(treeDepthRef.current);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);


  // Whole-facility tree for the range. Fetched only when the range changes
  // (and shared with Energy Breakdown); the branch is cut out below, so
  // switching branch never calls the API.
  const [rawTree, setRawTree] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!username || !dateRange?.start || !dateRange?.end) return;
      setLoading(true);
      setError(null);
      try {
        const formatDateTime = (d) => {
          if (!d) return "";
          const dateObj = typeof d === "string" ? new Date(d) : d;
          if (isNaN(dateObj.getTime())) return d;

          // Format as YYYY-MM-DD HH:mm:ss for backend compatibility
          const isoString = new Date(
            dateObj.getTime() - dateObj.getTimezoneOffset() * 60000,
          ).toISOString();
          return isoString.replace("T", " ").slice(0, 19);
        };
        const data = await fetchEnergyHierarchyTree(
          username,
          formatDateTime(dateRange.start),
          formatDateTime(dateRange.end),
        );
        if (!cancelled) setRawTree(data);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setError("Failed to load energy flow data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [username, dateRange]);

  // Scope to the selected branch's subtree (Historical branch filter), so the
  // flow diagram shows the branch instead of the whole facility.
  useEffect(() => {
    if (!rawTree) return;
    setHierarchyData(scopeHierarchyTree(rawTree, scopeHierarchyPath));
  }, [rawTree, scopeHierarchyPath]);

  const { chartData, nodeStyles } = useMemo(() => {
    if (!hierarchyData) return { chartData: { datasets: [] }, nodeStyles: {} };

    const rawPoints = [];
    const nodeStyles = {};
    const columnMap = {}; // pins every node to its tree level (column)
    const processedNodes = new Set();

    // Recursively traverse tree
    const traverseNode = (node, depth = 1) => {
      // Use maxLevels state
      if (depth > maxLevels) return;

      const nodeId = node.machine_name; // Use machine_name directly

      const isFirstVisit = !processedNodes.has(nodeId);
      if (isFirstVisit) {
        processedNodes.add(nodeId);

        const nodeEnergy = parseEnergy(node.energy);
        const nodeValue = isPkr
          ? nodeEnergy * rateForCategory(node.category, rates)
          : nodeEnergy;

        // Register node style — use machine color map first, then backend color, then palette
        nodeStyles[nodeId] = {
          label: `${node.machine_name} (${formatAmount(nodeValue)} ${valueUnit})`,
          color: machineColors[nodeId] || node.color || getNodeColor(depth),
        };

        // Pin this node to its tree level so it never drifts into another column
        columnMap[nodeId] = depth - 1;
      }

      // Create flows to children — keep zero-energy nodes too so they still show.
      // Skip children beyond the selected level (BEFORE pushing the flow) so the
      // level filter actually cuts deeper nodes instead of drawing them anyway.
      (node.children || []).forEach((child) => {
        const childId = child.machine_name;
        if (!childId) return;
        if (depth + 1 > maxLevels) return; // child is beyond the selected level

        const childEnergy = parseEnergy(child.energy);
        // A flow carries the energy that reaches the child, so it is priced at
        // the child's category rate - the same rate its own node label uses.
        const childValue = isPkr
          ? childEnergy * rateForCategory(child.category, rates)
          : childEnergy;
        rawPoints.push({ from: nodeId, to: childId, flow: childValue });
        if (!processedNodes.has(childId)) {
          traverseNode(child, depth + 1);
        }
      });
    };

    // Find root nodes based on hierarchy_path depth
    // Root nodes are those with the shortest hierarchy_path (usually depth 2 e.g., /root/)
    const nodes = Object.values(hierarchyData);
    const minPathLength = Math.min(
      ...nodes.map(
        (n) => (n.hierarchy_path || "").split("/").filter(Boolean).length,
      ),
    );
    const rootNodes = nodes.filter(
      (n) =>
        (n.hierarchy_path || "").split("/").filter(Boolean).length ===
        minPathLength,
    );

    rootNodes.forEach((root) => traverseNode(root));

    // Clamp tiny flows to a minimum visible thickness so they remain hoverable.
    // Keep realFlow for the tooltip so the user sees the true kWh.
    const maxFlow = rawPoints.reduce((m, p) => Math.max(m, p.flow), 0);
    const minVisible = maxFlow * 0.2; // 20% of largest flow — tiny flows large enough for label to fit
    // Zero-energy nodes get a thin visible band so they still appear (true kWh
    // stays 0 in the tooltip via realFlow).
    const zeroBand = maxFlow > 0 ? maxFlow * 0.04 : 1;
    const dataPoints = rawPoints.map((p) => ({
      from: p.from,
      to: p.to,
      flow: p.flow > 0 ? Math.max(p.flow, minVisible) : zeroBand,
      realFlow: p.flow,
    }));

    return {
      chartData: {
        datasets: [
          {
            label: "Energy Flow",
            data: dataPoints,
            colorFrom: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            colorTo: (c) => nodeStyles[c?.raw?.to]?.color || "#94a3b8",
            colorMode: "to",
            alpha: 0.6,
            hoverColorFrom: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            hoverColorTo: (c) => nodeStyles[c?.raw?.to]?.color || "#94a3b8",
            hoverColorMode: "to",
            hoverAlpha: 1,
            size: "max",
            borderWidth: 0,
            column: columnMap, // fix every node to its tree level (no drifting)
            labels: {
              formatter: (v) => nodeStyles[v]?.label || v,
              font: { size: isFullScreen ? 12 : 10, weight: "bold" },
              color: "#334155",
            },
            node: { width: isFullScreen ? 35 : 25, padding: isFullScreen ? 30 : 20 },
          },
        ],
      },
      nodeStyles,
    };
  }, [hierarchyData, isFullScreen, maxLevels, machineColors, isPkr, rates, valueUnit]);

  // Actual depth of the tree — so the Levels dropdown only offers levels that exist.
  const treeDepth = useMemo(() => {
    if (!hierarchyData) return 2;
    const nodes = Object.values(hierarchyData);
    if (!nodes.length) return 2;
    const minPathLength = Math.min(
      ...nodes.map(
        (n) => (n.hierarchy_path || "").split("/").filter(Boolean).length,
      ),
    );
    const roots = nodes.filter(
      (n) =>
        (n.hierarchy_path || "").split("/").filter(Boolean).length ===
        minPathLength,
    );
    const getDepth = (node, d = 1) => {
      const kids = node.children || [];
      if (!kids.length) return d;
      return Math.max(...kids.map((c) => getDepth(c, d + 1)));
    };
    const maxD = roots.length
      ? Math.max(...roots.map((r) => getDepth(r, 1)))
      : 2;
    return Math.max(2, maxD);
  }, [hierarchyData]);

  // Keep the ref current so the fullscreen handler can read the latest depth.
  useEffect(() => {
    treeDepthRef.current = treeDepth;
  }, [treeDepth]);

  // Only show level options that actually exist (2 … treeDepth).
  const levelOptions = useMemo(() => {
    const opts = [];
    for (let l = 2; l <= treeDepth; l += 1) opts.push(l);
    return opts.length ? opts : [2];
  }, [treeDepth]);

  // Default the dropdown to Full once the tree depth is known.
  // treeDepth falls back to 2 before the tree loads, so wait for real nodes --
  // otherwise we would lock in that placeholder and ignore the actual depth.
  useEffect(() => {
    if (levelsInitialisedRef.current) return;
    if (!hierarchyData || !Object.keys(hierarchyData).length) return;
    levelsInitialisedRef.current = true;
    setMaxLevels(treeDepth);
  }, [hierarchyData, treeDepth]);

  // Keep the selected level within the available range (except in fullscreen).
  useEffect(() => {
    if (!isFullScreen && maxLevels > treeDepth) {
      setMaxLevels(treeDepth);
    }
  }, [treeDepth, isFullScreen, maxLevels]);

  if (loading)
    return (
      <div
        style={{
          position: "relative",
          minHeight: 380,
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
        }}
      >
        <BoltLoader label="Loading Energy Flow…" />
      </div>
    );
  if (error)
    return (
      <div style={{ textAlign: "center", padding: 20, color: "red" }}>
        {error}
      </div>
    );
  if (!hierarchyData) return null;

  const flowRows = chartData?.datasets?.[0]?.data || [];

  return (
    <div
      ref={chartRef}
      style={{
        background: "#fff",
        borderRadius: isFullScreen ? 0 : 16,
        padding: isFullScreen ? 40 : 20,
        minHeight: isFullScreen ? "100vh" : 500,
        width: "100%",
        height: isFullScreen ? "100vh" : "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <h3 style={{ margin: 0 }}>
            <FieldHelp id="historical.energyFlow">
              {isPkr ? "Cost Flow Diagram" : "Energy Flow Diagram"}
            </FieldHelp>
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f8fafc", padding: "4px 12px", borderRadius: "20px", border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}><FieldHelp id="historical.flowLevels">Levels:</FieldHelp></span>
            <select 
              value={maxLevels} 
              onChange={(e) => setMaxLevels(Number(e.target.value))}
              style={{ border: "none", background: "transparent", fontSize: "12px", fontWeight: 700, color: "#0f172a", cursor: "pointer", outline: "none" }}
            >
              {levelOptions.map((l) => (
                <option key={l} value={l}>
                  {l === treeDepth ? `${l} (Full)` : l}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AIAnalyzerButton onClick={handleAnalyzeSankey} />
          <ChartBurgerMenu
            chartRef={chartRef}
            chartTitle={isPkr ? "Cost_Flow_Sankey" : "Energy_Flow_Sankey"}
            onAIAnalyze={handleAnalyzeSankey}
            exportContainer={true}
            chartData={chartData.datasets[0].data.map((d) => [
              d.from,
              d.to,
              d.flow,
            ])}
            chartType="sankey"
          />
        </div>
      </div>
      {flowRows.length > 0 ? (
        <div
          key={isFullScreen ? "full" : "dash"}
          style={{ flex: 1, minHeight: isFullScreen ? "80vh" : 250, width: "100%" }}
        >
          <Chart
            type="sankey"
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      if (ctx.raw?.flow !== undefined) {
                        const f = nodeStyles[ctx.raw.from]?.label || ctx.raw.from;
                        const t = nodeStyles[ctx.raw.to]?.label || ctx.raw.to;
                        const realValue = ctx.raw.realFlow ?? ctx.raw.flow;
                        return `${f} \u2192 ${t}: ${formatAmount(realValue)} ${valueUnit}`;
                      }
                      return ctx.label || "";
                    },
                  },
                },
              },
            }}
          />
        </div>
      ) : (
        <div
          style={{
            height: 450,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#64748b",
          }}
        >
          This hierarchy does not have enough layers to build a flow diagram.
        </div>
      )}
      <AIAnalyzerModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        chartPayload={aiModalPayload}
      />
    </div>
  );
};

export default SankeyDiagram;
